import type {
    IconDefinition,
    IconProp,
} from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tab } from "@headlessui/react";
import { type ReactNode, useEffect, useRef, useState } from "react";

interface Props {
    children: ReactNode;
    className?: string;
    compact?: boolean;
    flair?: {
        label: string;
        className: string;
    };
}

export const Card = (props: Props) => {
    return (
        <div
            className={`
                mt-6 p-5 rounded-xl
                bg-white text-xl drop-shadow-md
                ${props.compact ? "w-fit" : "w-full"}
                ${props.className || ""}
            `}
        >
            {props.children}
        </div>
    );
};

export const DashboardCard = (props: Props) => {
    return (
        <div
            className={`
                mt-2 relative rounded bg-white
                border border-gray-300
                ${props.compact ? "w-fit" : "w-full"}
                ${props.className}
            `}
        >
            {props.flair && (
                <>
                    <svg
                        className="absolute -ml-[8px] mt-[8px]"
                        width="8"
                        height="15"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <title>{props.flair.label}</title>
                        <polygon
                            points="0, 0 15, 15 15, 0"
                            fill="currentColor"
                            fillOpacity="0.8"
                        />
                    </svg>
                    <div
                        className={`absolute -mt-3 -ml-2 text-sm text-white font-bold px-2 rounded-t rounded-br ${props.flair.className}`}
                    >
                        {props.flair.label}
                    </div>
                </>
            )}
            {props.children}
        </div>
    );
};

interface DashboardTabsProps {
    tabs: {
        name: string;
        child: ReactNode;
        classname?: string;
        end?: boolean;
    }[];
}

export const DashboardTabs = (props: DashboardTabsProps) => (
    <Tab.Group>
        <Tab.List className="flex gap-1 text-sm mt-4">
            {props.tabs.map((t) => (
                <Tab
                    className={`
                        h-10 px-4 capitalize rounded-t border border-ae-gray-250 -mb-[1px]
                        ui-selected:text-ae-blue-500 ui-selected:border-b-white ui-selected:bg-white
                        ${t.classname}
                    `}
                    key={t.name}
                >
                    {t.name}
                </Tab>
            ))}
        </Tab.List>
        <Tab.Panels>
            {props.tabs.map((t) => (
                <Tab.Panel
                    key={t.name}
                    className="rounded bg-white border border-gray-300"
                >
                    {t.child}
                </Tab.Panel>
            ))}
        </Tab.Panels>
    </Tab.Group>
);

export const DashboardTabsV2 = (props: DashboardTabsProps) => (
    <Tab.Group>
        <Tab.List className="flex gap-1 text-sm px-2 mt-2">
            {props.tabs
                .filter((t) => !t.end)
                .map((t) => (
                    <Tab
                        className={`
                        h-10 px-4 capitalize border-ae-gray-250
                        ui-selected:text-ae-blue-550 border-b-2 border-b-transparent ui-selected:border-b-ae-blue-550
                        ${t.classname}
                    `}
                        key={t.name}
                    >
                        {t.name}
                    </Tab>
                ))}
            <div className="flex-grow" />
            {props.tabs
                .filter((t) => t.end)
                .map((t) => (
                    <Tab
                        className={`
                        h-10 px-4 capitalize border-ae-gray-250
                        ui-selected:text-ae-blue-550 border-b-2 border-b-transparent ui-selected:border-b-ae-blue-550
                        ${t.classname}
                    `}
                        key={t.name}
                    >
                        {t.name}
                    </Tab>
                ))}
        </Tab.List>
        <Tab.Panels>
            {props.tabs.map((t) => (
                <Tab.Panel
                    key={t.name}
                    className="rounded-b border-t border-gray-300"
                >
                    {t.child}
                </Tab.Panel>
            ))}
        </Tab.Panels>
    </Tab.Group>
);

type TabDef = {
    id: string;
    label: string;
    icon: IconProp;
    children: ReactNode;
    onClickIcon?: () => void;
    disabled?: boolean;
    hidden?: boolean;
};

interface CardWithTabsProps {
    tabs: TabDef[];
    title?: string;
    extraActions?: {
        title: string;
        icon: IconDefinition;
        action: () => void;
    }[];
    className?: string;
}

export const CardWithTabsUI = ({
    title,
    tabs,
    actualIndex,
    handleTabChange,
    className,
    extraActions,
}: {
    tabs: TabDef[];
    actualIndex: number;
    handleTabChange: (index: number) => void;
    title?: string;
    className?: string;
    extraActions?: {
        title: string;
        icon: IconDefinition;
        action: () => void;
    }[];
}) => (
    <div
        className={`w-full h-full min-h-0 flex flex-col bg-white border border-neutral-300 rounded-md drop-shadow-md overflow-hidden ${className || ""}`}
    >
        <Tab.Group selectedIndex={actualIndex} onChange={handleTabChange}>
            {/* Tab header */}
            <div className="p-0 pr-2 w-full min-h-7 flex justify-between border-b border-neutral-200 bg-neutral-50">
                <Tab.List className="flex-0 w-full min-h-7 flex items-center justify-start overflow-x-scroll text-sm text-neutral-600">
                    {title && <span className="pl-2 px-4">{title}</span>}
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.id}
                            disabled={tab.disabled}
                            className={({ selected }) => `
                                flex items-center text-start h-full gap-0.5 pl-1 pr-2 border-b outline-none ${
                                    tab.disabled
                                        ? "border-transparent text-gray-400 cursor-not-allowed"
                                        : selected
                                          ? "border-black cursor-pointer bg-gray-100"
                                          : "border-transparent hover:bg-neutral-100 hover:border-black cursor-pointer"
                                }`}
                        >
                            <button
                                type="button"
                                className={`flex items-center py-1 rounded ${tab.onClickIcon ? "hover:bg-neutral-200" : ""}`}
                                onClick={
                                    tab.onClickIcon
                                        ? (e) => {
                                              e.stopPropagation();
                                              tab.onClickIcon!();
                                          }
                                        : undefined
                                }
                            >
                                <FontAwesomeIcon
                                    icon={tab.icon}
                                    className="h-2"
                                    style={
                                        tab.onClickIcon
                                            ? { cursor: "pointer" }
                                            : undefined
                                    }
                                />
                            </button>
                            <span
                                className="line-clamp-1"
                                title={tab.label as string}
                            >
                                {tab.label}
                            </span>
                        </Tab>
                    ))}
                </Tab.List>

                {extraActions && (
                    <div className="flex items-center gap-1">
                        {extraActions.map((action) => (
                            <button
                                key={action.title}
                                type="button"
                                onClick={action.action}
                                className="p-0 flex items-center justify-center"
                            >
                                <FontAwesomeIcon
                                    icon={action.icon}
                                    className="h-3"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Tab Contents */}
            <Tab.Panels className="relative flex-1 overflow-hidden min-h-0">
                {tabs.map((tab) => (
                    <Tab.Panel
                        className="flex flex-col h-full w-full"
                        key={tab.id}
                    >
                        {tab.children}
                    </Tab.Panel>
                ))}
            </Tab.Panels>
        </Tab.Group>
    </div>
);

export const CardWithTabs = (props: CardWithTabsProps) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const prevTabIdsRef = useRef<string[]>([]);

    // Determine the actual selected index based on selectedId
    const visibleTabs = props.tabs.filter((t) => !t.hidden);
    const selectedIndex = selectedId
        ? visibleTabs.findIndex((tab) => tab.id === selectedId)
        : 0;
    const actualIndex = selectedIndex === -1 ? 0 : selectedIndex;

    useEffect(() => {
        const currentTabIds = props.tabs.map((tab) => tab.id);
        const prevTabIds = prevTabIdsRef.current;

        // Initial render - select first tab
        if (prevTabIds.length === 0) {
            setSelectedId(visibleTabs[0]?.id || null);
            prevTabIdsRef.current = currentTabIds;
            return;
        }

        // Find newly added tabs
        const newTabIds = currentTabIds.filter(
            (id) => !prevTabIds.includes(id),
        );

        // Check if currently selected tab still exists
        const selectedStillExists =
            selectedId && currentTabIds.includes(selectedId);

        if (newTabIds.length > 0) {
            // Switch to the first newly added tab
            setSelectedId(newTabIds[0]);
        } else if (!selectedStillExists && visibleTabs.length > 0) {
            // Selected tab was removed, select first available tab
            setSelectedId(visibleTabs[0].id);
        }

        prevTabIdsRef.current = currentTabIds;
    }, [props.tabs, selectedId, visibleTabs]);

    const handleTabChange = (index: number) => {
        setSelectedId(visibleTabs[index]?.id || null);
    };

    return (
        <CardWithTabsUI
            title={props.title}
            tabs={visibleTabs}
            actualIndex={actualIndex}
            handleTabChange={handleTabChange}
            className={props.className}
            extraActions={props.extraActions}
        ></CardWithTabsUI>
    );
};

export const SimpleCard = ({
    titleElement,
    children,
    className,
}: {
    titleElement: ReactNode;
    children: ReactNode;
    className?: string;
}) => (
    <div
        className={`w-full h-full min-h-0 flex flex-col bg-white border border-neutral-300 rounded-md drop-shadow-md overflow-hidden ${className || ""}`}
    >
        <div className="flex-0 w-full min-h-7 bg-neutral-50 border-b border-neutral-200 flex items-center justify-start overflow-x-scroll text-sm text-neutral-600">
            {titleElement}
        </div>
        <div className="relative flex-1 overflow-hidden min-h-0 flex flex-col h-full w-full">
            {children}
        </div>
    </div>
);
